import React from "react";
import ReactDOM from "react-dom/client";

import App from "./App";
import "./styles/index.css";

import sso from "./auth/sso";
import { syncAuthToLocalStorage, clearAuthData } from "./auth/authSync";
import { enableHttpInterceptor } from "./utils/httpClient";

const POST_LOGIN_REDIRECT = "/apps/accounts";

/**
 * keycloak-js captures `window.location.href` at the moment `init()` runs and
 * uses it as the `redirect_uri` it sends to the IdP. After a successful sign-in
 * Keycloak redirects the browser back to that captured URL — there is no
 * second client-side hop where we could navigate to /apps/accounts.
 *
 * So we have to position the URL on the post-login landing route BEFORE
 * `sso.init()` runs. Deep links (e.g. /apps/accounts/abc123) are preserved;
 * only the empty landing routes ("/" or "/login") are rewritten.
 */
const preflightRedirectUri = (): void => {
  const { pathname, search, hash } = window.location;
  const isLandingRoute =
    pathname === "/" || pathname === "" || pathname === "/login";
  if (isLandingRoute) {
    window.history.replaceState(
      null,
      "",
      `${POST_LOGIN_REDIRECT}${search}${hash}`,
    );
  }
};

const renderApp = (): void => {
  ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  );
};

preflightRedirectUri();

/**
 * APP_INITIALIZER equivalent — matches the `Templatify_Angular_Reference`
 * `SsoService.init()` shape:
 *
 *   - `onLoad: 'login-required'` sends the unauthenticated browser STRAIGHT
 *     to the Keycloak login form — no portal middleman. After login Keycloak
 *     bounces back to the URL captured above (`http://localhost:4200/apps/accounts`).
 *   - `pkceMethod: 'S256'` mandatory for public clients.
 *   - `onTokenExpired` performs a one-shot refresh; on failure we re-enter
 *     Keycloak's flow (NOT the corporate portal) so the user comes back to
 *     the same page authenticated if their IdP session is still alive.
 *   - `onAuthRefreshSuccess` keeps localStorage in sync with every silent
 *     refresh.
 */
(async () => {
  try {
    const authenticated = await sso.init({
      onLoad: "login-required",
      pkceMethod: "S256",
      checkLoginIframe: false,
      silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
    });

    if (!authenticated) {
      console.warn("SSO init resolved unauthenticated; refresh to retry");
      return;
    }

    syncAuthToLocalStorage(sso);
    // Open the gate: from this point on every `httpClient` request will
    // read the token from localStorage and attach it as
    // `Authorization: Bearer <token>`. Before this call `resolveAuthToken`
    // short-circuits to `null`, which is why API requests fired during
    // bootstrap had no Authorization header.
    enableHttpInterceptor();

    sso.onTokenExpired = () => {
      sso
        .updateToken(30)
        .then((refreshed) => {
          if (refreshed) syncAuthToLocalStorage(sso);
        })
        .catch(() => {
          // Refresh failed (typically a Keycloak `/token` CORS rejection or
          // an expired refresh token). Re-enter Keycloak's flow so the user
          // comes back to the same page authenticated — going to the portal
          // here would strand them on a different OIDC client.
          clearAuthData();
          sso.login({
            redirectUri: `${window.location.origin}${POST_LOGIN_REDIRECT}`,
          });
        });
    };

    sso.onAuthRefreshSuccess = () => syncAuthToLocalStorage(sso);

    renderApp();
  } catch (err) {
    console.error("SSO init failed:", err);
  }
})();
